// screenDataCache — cache en memoria del "último dato bueno" por pantalla (F4.3b T3).
//
// LA REGLA UX (Carlos, cerrada): al navegar, el usuario ve la pantalla correcta
// AL INSTANTE o ve un skeleton honesto. PROHIBIDO el stale-then-swap (pintar
// dato viejo que luego "brinca" al llegar el fresco): un dato que cambia frente
// al usuario es una pantalla que miente. Por eso el cache tiene UNA sola regla
// de frescura:
//   - entrada con edad < SCREEN_CACHE_TTL_MS → se pinta tal cual, SIN re-fetch
//     al entrar (dentro de esa ventana los cambios relevantes llegan por los
//     eventos de invalidación de abajo o los provoca el propio usuario, que
//     escribe write-through vía setData/refetch);
//   - entrada vieja o inexistente → skeleton + fetch. Nunca ambas cosas.
//
// LA ÚNICA EXCEPCIÓN, Y ESTÁ FIRMADA (PEND-1 D1, Carlos, 2026-08-06): con
// DICTAMEN de sin señal, `usePendingSummary` sí pinta su entrada vencida como
// última foto conocida, bajo la nota de sin conexión y sin autorizar el vacío.
// No es un stale-then-swap —el dato va ANUNCIADO como viejo, que es lo contrario
// de hacerlo pasar por fresco— y se justifica en que sin red no pueden nacer
// pendientes nuevos. El porqué completo vive junto a la semilla, en
// `hooks/usePendingSummary.js`. Este módulo no cambia: sigue devolviendo la
// entrada marcada `fresh:false` y quien decide es el consumidor.
//
// LA SEGUNDA EXCEPCIÓN, TAMBIÉN FIRMADA (TANDA 19 / #211, Carlos, 2026-08-28):
// LA FOTO DE BODEGA. Una entrada puede entrar aquí con ORIGEN `bodega` —la
// hidratación de `lib/warehouse/hidratarCache.js`, que lee la bodega local del
// propio dueño al arrancar— y esa entrada es PINTABLE por vieja que sea.
//
// POR QUÉ NO ES EL `stale-then-swap` QUE ESTE ARCHIVO PROHÍBE, que es la
// pregunta correcta y merece contestarse entera:
//
//   · La regla de arriba tiene DOS mitades pegadas: "se pinta" y "no se
//     re-fetchea". Están pegadas porque nacieron juntas —una semilla de fetch
//     vivo dentro de su ventana no pudo cambiar sin que lo sepamos, así que
//     preguntar de nuevo sería gasto—. La foto de bodega SEPARA esas dos
//     mitades: se pinta Y se pregunta. Lo que el usuario ve primero es lo último
//     que esta app supo, y lo que ve después es la verdad del servidor, escrita
//     EN SU LUGAR, sin silueta en medio y sin remontaje.
//   · El daño que la regla previene es "una pantalla que miente": dato viejo
//     PRESENTADO COMO FRESCO, que brinca. Aquí la corrección va garantizada por
//     construcción, no por suerte — el consumidor recibe la marca y su efecto de
//     fetch corre igual (`hooks/useScreenData.js`).
//   · Y sólo cubre lo que el carril dejó al descubierto: una raíz DORMIDA que
//     hasta hoy nacía con una silueta —o, en Estudiar, con el chasis y la lista
//     vacía— teniendo el dato entero en el teléfono.
//
// EL ALCANCE ESTÁ CERRADO CON LLAVE, no por convención: sólo las dos llaves de
// `HYDRATABLE_KEYS` admiten origen de bodega. `settings`, `study-log`,
// `analytics-overview`, `pending-summary` y las del banco de grupos no pueden
// recibirlo aunque alguien lo intente — ver `hydrateScreenCache`.
//
// PROHIBIDO también (restricción de escala): prefetch especulativo de DATOS y
// polling nuevo. Este módulo solo REDUCE llamadas al backend (omite re-fetches
// dentro de la ventana de frescura y dedupe de fetches concurrentes); jamás
// agrega una.
//
// Es memoria pura: muere con el tab/reload (un reload siempre re-fetchea), no
// persiste nada y no comparte entre pestañas.

import { subscribePlanChanged } from './planSync';
import { NET_VERDICT, subscribeNetworkVerdict } from './offline/networkVerdict';
import { SCREEN_CORRECTED_EVENT, SESSION_COMMITTED_EVENT } from './screenEvents';

export const SCREEN_CACHE_TTL_MS = 30_000;

/** El ORIGEN de una entrada. Sin marca = fetch vivo, que es el 100% de las
 *  entradas de todos los caminos anteriores a la TANDA 19 y sigue siéndolo de
 *  todas las que no pasan por `hydrateScreenCache`. */
export const ORIGEN_BODEGA = 'bodega';

/** LAS ÚNICAS LLAVES QUE ADMITEN FOTO DE BODEGA, y la lista es la puerta.
 *
 *  Son las dos raíces del carril entre las que la gente de verdad desliza y que
 *  tienen ensamblador de bodega de LECTURA PURA. `analytics-overview` NO está y
 *  no es un olvido: su ensamblador (`analyticsOverviewFromWarehouse`) ESCRIBE
 *  —dispara `fireSettleExpiredExams`, que sella exámenes vencidos— y una
 *  hidratación de arranque tiene que ser lectura y nada más. Entra el día que
 *  tenga un ensamblador de lectura pura, no antes.
 *
 *  Las demás llaves del cache (`settings`, `study-log`, `pending-summary` y las
 *  del banco de grupos) quedan fuera por la misma puerta: aquí no hay política
 *  por convención, hay una lista. */
export const HYDRATABLE_KEYS = Object.freeze(['study-center', 'dashboard']);

const cache = new Map(); // key → { data, at, origen? }
const inflight = new Map(); // key → Promise (dedupe de fetches concurrentes)
// Generación de sesión: se incrementa en cada purga de frontera. Un fetch en
// vuelo captura la generación al arrancar y solo escribe al cache si sigue
// vigente (ver clearScreenDataCache).
let generation = 0;

/**
 * Purga de FRONTERA DE SESIÓN — el único punto de invalidación total.
 *
 * Bug que motiva esto (QA on-device, TestFlight 0.9.0): las llaves del cache
 * identifican PANTALLA, no usuario ('dashboard', 'study-center'…), así que un
 * cambio de identidad dentro de la ventana del TTL hacía que la sesión nueva
 * pintara los datos cacheados de la anterior — un guest recién creado veía los
 * documentos (títulos/metadata) de la cuenta que acababa de cerrar sesión. En
 * un dispositivo compartido eso es un problema de PRIVACIDAD, no solo de
 * frescura. AuthContext llama esta función en TODA transición de identidad
 * (login, logout, guest, verify, OAuth, conversión guest→registrado, sesión
 * expirada); tras la purga cada pantalla cae a su path normal vacío →
 * skeleton → fetch.
 *
 * Además de vaciar las entradas: se descartan los fetches en vuelo (la promesa
 * de la sesión anterior no se puede cancelar, pero al avanzar `generation` su
 * resultado ya NO se escribe al cache — sin esto, un fetch lento del usuario
 * anterior repoblaría el cache DESPUÉS de la purga) y se libera el dedupe (un
 * fetch de la sesión nueva no debe compartir la promesa de la vieja).
 */
export function clearScreenDataCache() {
  generation++;
  cache.clear();
  inflight.clear();
}

/**
 * F6.7b post-smoke — SUELTA EL DEDUPE, conserva el dato.
 *
 * El dedupe de `inflight` existe para que varios consumidores que montan a la
 * vez compartan UNA petición. Eso es correcto mientras la petición esté viva;
 * deja de serlo cuando la red se fue y volvió: una lectura nueva, disparada
 * después de recuperar la señal, se pegaría a la promesa ZOMBI de antes del
 * corte — que en el mejor caso ya expiró y en el peor sigue colgada agotando su
 * techo. El usuario ve el esqueleto otra vez, con red.
 *
 * Esto NO es `clearScreenDataCache`: aquel purga el dato y avanza la
 * generación (frontera de identidad, privacidad). Este solo suelta el dedupe.
 * El dato pintado se queda donde está — recuperar la señal no es motivo para
 * blanquear una pantalla.
 *
 * Tampoco hace falta avanzar la generación: si una promesa vieja llega a
 * resolver bien, su dato es dato de servidor legítimo y escribirlo al cache no
 * miente; si fue abortada, rechaza y no escribe nada. Y el `finally` de
 * `fetchScreenData` solo borra su PROPIA entrada, así que una promesa vieja no
 * desaloja a la nueva.
 */
export function clearInflightScreenData() {
  inflight.clear();
}

/**
 * Lee una entrada. Devuelve `{ data, fresh, foto }` o `null` si no existe.
 *
 * `fresh` es la regla de siempre para todo lo que salió de un fetch vivo: la
 * ventana de `SCREEN_CACHE_TTL_MS`, sin un carácter de diferencia.
 *
 * `foto` es la marca de la TANDA 19 (ver el letrero de arriba): la entrada vino
 * de la bodega local. Una foto sale SIEMPRE `fresh: true` —es pintable por vieja
 * que sea, que es la mitad firmada— y su `foto: true` es lo que le dice al
 * consumidor la OTRA mitad: que además hay que preguntar. Quien no mire `foto`
 * se comporta exactamente como antes de esta tanda, y eso es a propósito: las
 * llaves de `HYDRATABLE_KEYS` son las únicas que pueden traerla.
 *
 * LA LLAVE `foto` SÓLO EXISTE CUANDO ES VERDAD, y no es coquetería: así el
 * objeto que reciben los caminos de siempre —las cinco pantallas que no hidratan
 * y los cuatro consumidores directos— es CARÁCTER POR CARÁCTER el de antes de
 * esta tanda. Un `foto: false` de relleno habría cambiado la forma de un retorno
 * que ya tiene pruebas de igualdad exacta, y con ella el contrato de todos.
 */
export function readScreenCache(key) {
  const entry = cache.get(key);
  if (!entry) return null;
  if (entry.origen === ORIGEN_BODEGA) {
    return { data: entry.data, fresh: true, foto: true };
  }
  return { data: entry.data, fresh: Date.now() - entry.at < SCREEN_CACHE_TTL_MS };
}

/** Escribe el último dato bueno (write-through desde fetches y mutaciones del usuario). */
export function writeScreenCache(key, data) {
  cache.set(key, { data, at: Date.now() });
}

/**
 * LA FOTO DE BODEGA ENTRA POR AQUÍ, Y SÓLO POR AQUÍ (TANDA 19 / #211).
 *
 * Tres candados, y los tres son la razón de que esta función exista en vez de un
 * parámetro más en `writeScreenCache`:
 *
 *   1. LA LLAVE TIENE QUE ESTAR EN LA LISTA. Fuera de `HYDRATABLE_KEYS` no se
 *      escribe nada y se contesta `false`. Ninguna de las otras pantallas del
 *      cache puede recibir origen de bodega ni por accidente ni a propósito.
 *   2. EL DATO VIVO SIEMPRE GANA. Si ya hay una entrada de fetch vivo, la foto
 *      NO la pisa: lo que el servidor dijo manda sobre lo que el teléfono
 *      recuerda, sin importar el orden en que lleguen. Entre dos fotos gana la
 *      nueva, que es la misma regla mirada de cerca.
 *   3. LA GENERACIÓN. La hidratación es asíncrona (la bodega vive detrás del
 *      puente nativo), así que entre que empezó a leer y terminó pudo haber una
 *      frontera de identidad. Se pasa la generación con la que arrancó y si ya
 *      no es la vigente, la escritura se descarta — el mismo candado con el que
 *      `fetchScreenData` impide que un fetch lento repueble un cache purgado.
 *
 * Devuelve si escribió, para que quien hidrata pueda contarlo sin adivinar.
 */
export function hydrateScreenCache(key, data, startedGeneration = generation) {
  if (!HYDRATABLE_KEYS.includes(key)) return false;
  if (data === undefined || data === null) return false;
  if (startedGeneration !== generation) return false;
  const previa = cache.get(key);
  if (previa && previa.origen !== ORIGEN_BODEGA) return false;
  cache.set(key, { data, at: Date.now(), origen: ORIGEN_BODEGA });
  return true;
}

/** La generación vigente, para que la hidratación pueda capturarla antes de
 *  irse a leer la bodega y descartar su escritura si cambió mientras leía. */
export function currentScreenCacheGeneration() {
  return generation;
}

/** Actualiza una entrada EXISTENTE con un updater; si no hay entrada, no hace nada. */
export function updateScreenCache(key, updater) {
  const entry = cache.get(key);
  if (!entry) return;
  cache.set(key, { data: updater(entry.data), at: Date.now() });
}

/** Borra entradas (el dato ya no es confiable → la próxima visita muestra skeleton+fetch). */
export function invalidateScreenData(...keys) {
  if (keys.length === 0) { cache.clear(); return; }
  keys.forEach((k) => cache.delete(k));
}

/**
 * Fetch con dedupe: si ya hay un fetch en vuelo para la key, se comparte esa
 * promesa (varios consumidores montando a la vez = UNA llamada, no N).
 * El resultado se escribe al cache; el error se propaga a todos.
 */
export function fetchScreenData(key, fetcher) {
  if (inflight.has(key)) return inflight.get(key);
  const startedGeneration = generation;
  const p = (async () => {
    try {
      const data = await fetcher();
      // Solo escribe si NO hubo frontera de sesión mientras el fetch volaba
      // (ver clearScreenDataCache: un resultado de la identidad anterior no
      // debe repoblar el cache ya purgado).
      if (startedGeneration === generation) writeScreenCache(key, data);
      return data;
    } finally {
      // Borra solo la PROPIA promesa: tras una purga, esta key puede ya tener
      // un fetch nuevo de la sesión siguiente que no hay que desalojar.
      if (inflight.get(key) === p) inflight.delete(key);
    }
  })();
  inflight.set(key, p);
  return p;
}

// ── Invalidación por eventos ─────────────────────────────────────────────────
// ÚNICO punto de suscripción: los eventos ya existentes del repo invalidan aquí
// las pantallas cuyos datos dependen de ellos, estén o no montadas. Las
// pantallas montadas que además quieren refrescarse EN VIVO mantienen su propio
// listener (Dashboard ya lo tenía; StudyCenter lo gana en T4) — eso es refresco
// por hecho consumado, no polling.
const EVENT_INVALIDATIONS = [
  // El poller de NotificationListener detecta doc listo → las listas de docs
  // cacheadas dejan de ser confiables.
  //
  // TANDA 15 (#189a) — Y `analytics-overview` CON ELLAS. Este aviso significa
  // "la lista de documentos de este usuario ya no es la que creías" —lo emiten
  // el borrado del Panel (`notifyDocsChanged`) y el poller al terminar de
  // procesar—, y el overview de Analíticas pinta UNA TARJETA POR DOCUMENTO más
  // los tres totales del encabezado: es tan lista-de-docs como las otras dos.
  // Sin esto, un documento borrado seguía vivo en su tarjeta hasta que la copia
  // guardada envejecía sola (30 s).
  //
  // INVALIDACIÓN Y NADA MÁS, por la regla escrita de la pieza 2 (abajo): los
  // números AGREGADOS de esta llave no se parchan a mano jamás.
  //
  // Y NO SE CONTAGIA A LAS VECINAS: `doc-uploaded` y `local-material-ready`
  // conservan sus dos llaves. Un documento recién subido no tiene nada que
  // enseñar en Analíticas —ni sesiones, ni readiness— y bajar su material a la
  // bodega tampoco cambia un solo número de esta pantalla; tirarles la llave
  // sería una re-descarga completa a cambio de nada.
  { target: 'window', event: 'studybuddy:docs-changed', keys: ['dashboard', 'study-center', 'analytics-overview'] },
  // F6.7a: el material de un doc terminó de bajar a la bodega — las listas
  // cacheadas dejan de ser confiables aunque nadie esté montado. MISMA señal
  // que refresca las pantallas montadas: una sola, un solo repintado.
  { target: 'window', event: 'studybuddy:local-material-ready', keys: ['dashboard', 'study-center'] },
  // Upload/finalize-split recién disparado → habrá docs nuevos en processing.
  { target: 'document', event: 'studybuddy:doc-uploaded', keys: ['dashboard', 'study-center'] },
  // Mutaciones del carril de pendientes.
  { target: 'window', event: 'studybuddy:pending-changed', keys: ['pending-summary', 'dashboard'] },
  // F6.8 S-2 (pieza 1) — MIS PROPIAS ESCRITURAS INVALIDAN. Una sesión de estudio
  // que se entrega, se cierra o se abandona ensucia justo estas tres pantallas, y
  // hasta aquí NINGÚN camino del carril de estudio tocaba una sola llave: el
  // usuario entregaba y el Study Log seguía pintando "en curso" desde la copia
  // guardada. El aviso lo emiten los runtimes y las páginas
  // (`lib/screenEvents.js`); el cache es quien decide qué llaves caen, que es la
  // misma división de siempre.
  //
  // SON TRES, Y NI UNA MÁS (censo S2-C9/S2-C10):
  //   · `study-log` — aparece o cambia un renglón;
  //   · `study-center` — su `completedSessions` sale de `/study/history`
  //     filtrado por `status === 'completed'`;
  //   · `analytics-overview` — cambian `doc_cards` (last_studied, has_exam_data,
  //     best_exam_score) y los tres totales del encabezado.
  // `dashboard` NO: el Panel no pinta un solo dato derivado de sesiones, así que
  // invalidarlo sería una re-descarga completa de `/documents` a cambio de nada
  // —y un esqueleto gratis en una pantalla que hoy pinta al instante—.
  // `settings` tampoco. Y `pending-summary` menos: su semilla fresca cuenta como
  // fetch para el throttle de refoco, y borrarla sin motivo lo desalinea.
  //
  // El espejo literal de NET.UP (las seis llaves) NO aplica aquí: allá la premisa
  // es "todo lo que pinté sin red es sospechoso"; aquí es "yo acabo de escribir
  // ESTO". Lo reusable es la FORMA, no la lista.
  //
  // `study-log` NO está en esta lista, y no es un olvido: lo atiende
  // `aplicarCierreAlStudyLog` (pieza 2), que PARCHA la fila propia en vez de
  // borrar la copia entera, y sólo invalida cuando no puede probar el parche.
  // Borrar aquí e intentar parchar allá, en cualquier orden, dejaría el parche
  // en letra muerta.
  { target: 'window', event: SESSION_COMMITTED_EVENT, keys: ['study-center', 'analytics-overview'] },
];

// TANDA 15e (#195) — Y EL AVISO SUELTA LA PETICIÓN ZOMBI, ANTES DE INVALIDAR.
//
// LA CARRERA. `fetchScreenData` comparte por llave el vuelo en curso, e
// `invalidateScreenData` borra la ENTRADA pero no soltaba ese dedupe. Secuencia
// real: sale un `GET /analytics/overview` → el usuario borra un documento →
// llega el aviso → la pantalla re-pide… y se PEGA a la petición que salió ANTES
// del borrado. La respuesta trae el documento muerto, se pinta, y se re-cachea
// como fresca otros 30 s. La invalidación quedaba en letra muerta justo cuando
// más importaba.
//
// VA EN EL MECANISMO, NO EN UNA FILA: una invalidación que deja pegarse a un
// zombi es el MISMO bug en `dashboard`, en `study-center` y en
// `pending-summary`. Es exactamente el orden que el suscriptor de NET.UP ya
// tenía escrito unas líneas más abajo, con su letrero.
//
// LA UTILERÍA ES GLOBAL, no por llave, y aquí eso no hace daño: soltar el vuelo
// no cancela ni descarta nada —quien ya tiene la promesa la sigue teniendo, y su
// `finally` sólo borra su propia entrada—; a lo sumo cuesta que una lectura
// nueva de otra pantalla salga a la red en vez de pegarse a un vuelo ajeno, que
// es el lado seguro del error.
if (typeof window !== 'undefined') {
  EVENT_INVALIDATIONS.forEach(({ target, event, keys }) => {
    const t = target === 'window' ? window : document;
    t.addEventListener(event, () => {
      clearInflightScreenData();
      invalidateScreenData(...keys);
    });
  });
}

// ── F6.8 S-2 (pieza 2) — LA VERDAD PROPIA, ESCRITA ENCIMA ───────────────────
//
// POR QUÉ NO BASTA CON INVALIDAR. Invalidar sólo garantiza que la próxima
// entrada vuelva a PREGUNTAR — y en el síntoma (b) del smoke el problema no era
// la pregunta sino la respuesta: el usuario cierra la adaptiva en el cel, el
// acta queda en la bodega y el cartero la sube con debounce (2.5 s / 10 s
// mínimo). Durante esos segundos `GET /study/history` devuelve la sesión EN
// CURSO con 11 respuestas — una respuesta incorrecta pero legítima, que además
// se re-cachea y vive otros 30 s. Un re-fetch le vuelve a preguntar al mismo
// mentiroso (censo S2-C11).
//
// LO QUE ESTA PIEZA HACE: la app escribe lo que SABE. Acaba de cerrar ESA
// sesión, así que corrige ESA fila de la copia guardada y no toca nada más.
//
// LAS TRES REGLAS QUE LA MANTIENEN HONESTA:
//
//   1. SÓLO LA FILA PROPIA, y sólo con datos que la app tiene por CERTEZA del
//      resultado del submit/cierre (estado terminal, calificación y contadores
//      finales — `sessionCommittedPatch` los recorta en el emisor). Las demás
//      filas se quedan exactamente como estaban.
//   2. SIN COPIA, NADA. Si la llave no tiene entrada, no se crea: la próxima
//      entrada fetchea limpio, que es el comportamiento de siempre.
//   3. SI NO PUEDO PROBAR EL PARCHE, INVALIDO. Si la copia existe pero no
//      contiene esa fila (una sesión nacida después de la última lectura — el
//      caso del examen entregado sin señal), parchar no la haría aparecer y
//      dejar la copia fresca ESCONDERÍA la sesión hasta 30 s más. Ahí se borra
//      la entrada, que es lo correcto y lo que hacía la pieza 1.
//
// Y LO QUE ESTÁ PROHIBIDO, por escrito: parchar a mano los números AGREGADOS de
// `analytics-overview` (totales, `doc_cards`, mejor score) o el
// `completedSessions` de `study-center`. Esos salen de una derivación del
// núcleo sobre TODAS las sesiones; reconstruirlos aquí sería una segunda
// implementación de la matemática del producto. Esas dos llaves reciben
// invalidación y nada más.
//
// ÉSTE ES EL ÚNICO PUNTO DEL MÓDULO QUE CONOCE UNA FORMA. El cache es agnóstico
// de shapes por diseño; la excepción se documenta aquí y se justifica sola: sin
// saber que `study-log` es un arreglo de sesiones con `id` no hay forma de
// corregir una fila sin borrar las otras.
function aplicarCierreAlStudyLog(patch) {
  if (!patch || !patch.id) { invalidateScreenData('study-log'); return; }
  const entry = cache.get('study-log');
  if (!entry) return;                       // regla 2
  const rows = entry.data;
  if (!Array.isArray(rows) || !rows.some((r) => r && r.id === patch.id)) {
    invalidateScreenData('study-log');      // regla 3
    return;
  }
  updateScreenCache('study-log', (prev) => prev.map(   // regla 1
    (r) => (r && r.id === patch.id ? { ...r, ...patch } : r)));
}

if (typeof window !== 'undefined') {
  window.addEventListener(SESSION_COMMITTED_EVENT, (e) => {
    aplicarCierreAlStudyLog(e?.detail?.session);
  });
}

// ── F6.8 S-2 (pieza 3) — LA CORRECCIÓN DE UNA LECTURA SERVIDA POR LA FOTO ───
//
// Cuando el motor rechaza, la lectura de bodega cae a la FOTO — que no fusiona
// entregas locales ni sella vencidos— y hasta hoy NADIE se enteraba. Si el motor
// responde después para esa misma lectura, su verdad llega por este evento.
//
// AQUÍ NO SE CREA ENTRADA. Igual que el parche de arriba: sin copia guardada no
// hay nada que corregir y la próxima entrada fetchea limpio. La pantalla que SÍ
// está montada hace su propio write-through por `setData`, que es para lo que
// esa primitiva existe — ahí el dato sí se está pintando.
if (typeof window !== 'undefined') {
  window.addEventListener(SCREEN_CORRECTED_EVENT, (e) => {
    const { key, data } = e?.detail || {};
    if (!key || data === undefined) return;
    updateScreenCache(key, () => data);
  });
}

// Cambio de plan (upgrade/downgrade en otra pestaña vía planSync): créditos,
// topes y suscripción cambiaron → todo lo que los pinta se invalida. El flujo
// same-tab de Stripe recarga la página completa (el cache muere solo).
if (typeof window !== 'undefined') {
  subscribePlanChanged(() => invalidateScreenData('dashboard', 'settings', 'study-center'));
}

// F6.7b post-smoke — la señal VOLVIÓ: ninguna lectura nueva debe pegarse a una
// petición zombi de antes del corte (ver clearInflightScreenData). También se
// invalidan las listas: el dato que se pintó sin red es la última foto conocida,
// no la verdad de ahora, así que la próxima lectura debe ir al servidor en vez
// de reusar una entrada "fresca" dentro de la ventana de 30 s.
subscribeNetworkVerdict((verdict) => {
  if (verdict !== NET_VERDICT.UP) return;
  clearInflightScreenData();
  invalidateScreenData('dashboard', 'study-center', 'study-log', 'analytics-overview', 'pending-summary', 'settings');
});
